import compact from "lodash/compact";

/**
 * @description Takes an array of addresses (Objects) and returns an Object of address components
 * (city, country_code, country, formattedAddress, province).
 *
 * @param {Array<Objects>}  addresses           An array of Objects
 * @param {string}          address.types       Value determines how to configure component name
 * @param {string}          address.short_name  Short name of a city/town
 * @param {string}          address.long_name   Long name of a country
 *
 * @return {Object} An object with city, country_code, country, formattedAddress, and province
 * properties
 */
export const getAddressComponents = (addresses) => {
  let country = "";
  let city = "";
  let town = "";
  let region = "";
  let province = "";
  let countryCode = "";
  addresses.forEach((address) => {
    if (address.types[0] === "locality") {
      city = address.short_name;
    } else if (!city && address.types[0] === "postal_town") {
      city = address.short_name;
    } else if (address.types[0] === "administrative_area_level_3") {
      town = address.short_name;
    } else if (address.types[0] === "administrative_area_level_2") {
      region = address.short_name;
    } else if (address.types[0] === "administrative_area_level_1") {
      province = address.short_name;
    } else if (address.types[0] === "country") {
      country = address.long_name;
      countryCode = address.short_name;
    }
  });
  const placeName = city || town || region; // we want to use city name, then town, then region
  return {
    formattedAddress: `${compact([placeName, province, country]).join(", ")}`,
    city: placeName,
    province,
    country,
    country_code: countryCode,
  };
};

/**
 * @description Takes a google place Object and returns an Object with various address components
 * (googlePlceId, street, postalCode, city, province, country, latitude, longitude).
 *
 * @param {Object} place                               A google place object
 * @param {Array}  place.address_components            An array of Objects
 * @param {Array}  place.address_components.types      Type of address component
 *                                                     (province, country etc)
 * @param {string} place.address_components.short_name Name of address component
 * @param {string} place.place_id                      Id of google place object
 * @param {Object} place.geometry                      Contains lat and lng info
 * @param {Object} place.geometry.location             Contains lat and lng functions
 * @param          place.geometry.location.lat         Function provided by google place object
 * @param          place.geometry.location.lng         Function provided by google place object
 *
 * @return {Object} Object of address components.
 */
export const parseGoogleAddress = (place) => {
  const components = {};
  place.address_components.forEach((component) => {
    components[component.types[0]] = component.short_name;
  });
  const street = components.street_number
    ? `${components.street_number} ${components.route}`
    : "";
  return {
    googlePlaceId: place.place_id,
    street,
    postalCode: components.postal_code,
    city: components.locality,
    province: components.administrative_area_level_1,
    country: components.country,
    latitude: place.geometry.location.lat().toString(),
    longitude: place.geometry.location.lng().toString(),
  };
};

/**
 * @summary Parses google hours into a formatted Object where keys are the days of the week and
 * values are the open and closing time for the day.
 *
 * @param {Object}  openingHours               Object that contains an Array of Objects
 * @param {Array}   openingHours.periods       Array of Objects - the days opening and closing times
 * @param {Object}  openingHours.periods.open  contains key/value pairs for day and opening time
 * @param {Object}  openingHours.periods.close contains key/value pair of the closing time
 *
 * @return {Object} An Object with the days of the week as a key and the open and close times
 * as the values
 */
export const parseGoogleHours = (openingHours) => {
  const result = {};
  openingHours?.periods.forEach((weekDay) => {
    result[weekDay.open.day] = {
      open: weekDay.open.time,
      close: weekDay.close.time,
    };
  });
  return result;
};

// NOTE - if window.google has not loaded yet then this function will do nothing
export const getGooglePlaceFromLocationName = ({ city, province }, setter) => {
  if (window.google) {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: `${city} ${province}` }, (results, status) => {
      if (status === window.google.maps.GeocoderStatus.OK && results.length) {
        const {
          city: formattedCity,
          country: formattedCountry,
          province: formattedProvince,
        } = getAddressComponents(results[0].address_components);
        setter({
          city: formattedCity,
          country: formattedCountry,
          province: formattedProvince,
          lat: results[0].geometry.location.lat().toString(),
          lng: results[0].geometry.location.lng().toString(),
        });
      }
    });
  }
};
